import React, { useState, useRef } from "react";
import NavbarGuest from "./NavbarGuest";
import Progress from "./Progress";
import Thankyou from "./Thankyou";
import { Personal, TermsofUse, Organization, Password } from "./Forms";
import Footer from "./Footer";
import OrgDetailsDialog from "./OrgDetailsDialog";
import useResponsiveFlags from "../hooks/useResponsiveFlags";
import { isBlockedEmailDomain, checkEmailRegex } from "./utilities/emailUtilis";
import { Link } from "react-router-dom";
import axios from "../api/axios";

export default function Register() {
  const { isMobile } = useResponsiveFlags();
  // State to store the current step

  const [step, setSteps] = useState(1);
  const [isRegistered, setIsRegistered] = useState(false);
  const [showOrgDetails, setShowOrgDetails] = useState(false);
  const [orgData, setOrgData] = useState();
  const [isLoading, setIsLoading] = useState(false);

  const [emailCheckRequireCaptcha, setEmailCheckRequireCaptcha] = useState(false);
  const [emailCaptchaToken, setEmailCaptchaToken] = useState("");

  const totalSteps = 4;

  // State to store user details
  const [formData, setFormData] = useState({
    termsAccepted: false,
    personalInfo: {
      firstName: "",
      lastName: "",
      email: "",
    },
    organizationInfo: {
      orgName: "",
      orgCode: "",
      address: "",
      city: "",
      state: "",
      zip: "",
      country: "",
    },
    passwordInfo: {
      password: "",
      password_confirmation: "",
      captcha: "",
    },
  });
  const [errors, setErrors] = useState({});
  const captchaRef = useRef(null);
  const emailCaptchaRef = useRef(null);

  const handleCloseOrgDeatailsDialog = (e) => {
    if (e) e.preventDefault();
    setShowOrgDetails(false);
  };
  // Handle data updates for each step
  const updateFormData = (newData) => {
    const fieldName = Object.keys(newData)[0];
    setFormData((prevData) => ({
      ...prevData,
      ...newData,
    }));

    setErrors((prevErrors) => ({
      ...prevErrors,
      [fieldName]: "",
    }));
  };

  function handlePrev() {
    if (step > 1) setSteps((step) => step - 1);
  }

  async function handleNext() {
    if (!validateStep()) return;
    if(step === 2){
      await checkEmailAvailability();
      return;
    }
    if (step === 3) {
      await checkOrganization();
      setFormData(prev => ({
        ...prev,
        passwordInfo: {
          ...prev.passwordInfo,
          captcha: "",
        },
      }));
      return;
    }
    if (step === totalSteps) {
      await register();
      return;
    }
    setSteps(prev => prev + 1);
  }

  const checkEmailAvailability = async () => {
    setIsLoading(true);
    setErrors({});

    try {
      await axios.get(`/sanctum/csrf-cookie`);
      const payload = { email: formData.personalInfo.email };

      if(emailCheckRequireCaptcha && emailCaptchaToken){
        payload.captcha_token = emailCaptchaToken;
      }

      const response = await axios.post(`/check-email`, payload);

      if(response.data.exists){
        setErrors({ email: "This email is already registered. Please log in or use a different email."});
        return;
      }
      setEmailCheckRequireCaptcha(false);
      setEmailCaptchaToken("");
      setSteps(prev => prev+1);
    } catch (error) {
      const status = error.response?.status;
      const data = error.response?.data;
      if(status === 429 && data?.require_captcha){
        setEmailCheckRequireCaptcha(true);
        setErrors({ email: data.message || "Too many attempts. Please complete the captcha to continue."});
        return;
      }
      if(status === 422 && data?.require_captcha){
        setEmailCaptchaToken("");
        setErrors({ email: data.message || "Invalid captcha. Please try again."});
        return;
      }
      if( data?.message){
        setErrors({ general: data.message});
      }else{
        setErrors({ general: "Error checking email. Please try again."});
      }
    }finally{
      emailCaptchaRef.current?.reset();
      setIsLoading(false);
    }
  }

  const checkOrganization = async () => {
    setIsLoading(true);
    setErrors({});

    try {
      await axios.get(`/sanctum/csrf-cookie`, {});
      // Check if org code is provided
      if (formData.organizationInfo.orgCode && formData.organizationInfo.orgCode.trim()) {
        const response = await axios.post(`/check-org-code`,
          { orgcode: formData.organizationInfo.orgCode, }
        );
        if (response.data.exists) {
          const org = response.data.organization;
          setOrgData([org]);
          setShowOrgDetails(true);
          return;
        } else {
          setErrors({
            orgCode: "Organization code not found. Please check and try again.",
          });
        }
      }
      // Check if org name is provided
      else if (formData.organizationInfo.orgName && formData.organizationInfo.orgName.trim()) {
        const response = await axios.post(`/search-orgs`, { orgname: formData.organizationInfo.orgName });
        if (response.data.found && response.data.organizations.length > 0) {
          setOrgData(response.data.organizations);
          setShowOrgDetails(true);
          return;
        } else {
          setShowOrgDetails(false);
          setSteps(4);
        }
      } else {
        setErrors({
          general: "Please provide either an organization code or orgnisation name.",
        });
      }
    } catch (error) {
      console.error("Response data:", error.response?.data);
      console.error("Response status:", error.response?.status);

      if (error.response?.data?.message) {
        setErrors({ general: error.response.data.message });
      } else {
        setErrors({ general: "Error checking organization. Please try again." });
      }
    } finally {
      setIsLoading(false);
    }
  };
  const capitalize = (value) => {
    if (!value) return value;

    return value
      .toLowerCase()
      .split(" ")
      .filter(Boolean)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };
  const validateStep = () => {
    let tempErrors = {};

    if (step === 1) {
      if (!formData.termsAccepted)
        tempErrors.termsAccepted = "Please accept the terms and conditions";
    }
    if (step === 2) {
      let firstName = formData.personalInfo.firstName?.trim();
      let lastName = formData.personalInfo.lastName?.trim();
      const email = formData.personalInfo.email?.trim();

      firstName = capitalize(firstName);
      lastName = capitalize(lastName);
      setFormData(prev => ({
        ...prev,
        personalInfo: {
          ...prev.personalInfo,
          firstName,
          lastName
        },
      }));

      const nameRegex = /^[a-zA-Z\s'-]+$/;

      if (!firstName) {
        tempErrors.firstName = "First name is required";
      } else if (firstName.length < 2) {
        tempErrors.firstName = "First name must be at least 2 characters long";
      } else if (firstName.length > 30) {
        tempErrors.firstName = "First name must be less than or equal to 30 characters";
      } else if (!nameRegex.test(firstName)) {
        tempErrors.firstName = "Invalid characters in first name";
      }

      if (!lastName) {
        tempErrors.lastName = "Last name is required";
      } else if (lastName.length < 2) {
        tempErrors.lastName = "Last name must be at least 2 characters long";
      } else if (lastName.length > 30) {
        tempErrors.lastName = "Last name must be less than or equal to 30 characters";
      } else if (!nameRegex.test(lastName)) {
        tempErrors.lastName = "Invalid characters in last name";
      }

      if (!email) {
        tempErrors.email = "Email is required";
      } else if (!checkEmailRegex(email)) {
        tempErrors.email = "Email is invalid";
      } else if (email.length > 255) {
        tempErrors.email = "Email must be less than 255 characters";
      } else if (isBlockedEmailDomain(email)) {
        tempErrors.email = "Personal email domains are not allowed";
      }
    }

    if (step === 3) {
      if (!formData.organizationInfo.orgCode) {
        if (!formData.organizationInfo.orgName)
          tempErrors.orgName = "Organization name is required";
        if (!formData.organizationInfo.address)
          tempErrors.address = "Address is required";
        if (!formData.organizationInfo.city)
          tempErrors.city = "City is required";
        if (!formData.organizationInfo.state)
          tempErrors.state = "State is required";
        if (!formData.organizationInfo.zip)
          tempErrors.zip = "Zip is required";
        if (!formData.organizationInfo.country)
          tempErrors.country = "Country is required";
      }
    }

    if (step === 4) {
      if (!formData.passwordInfo.password) {
        tempErrors.password = "Password is required";
      } else if (formData.passwordInfo.password.length < 8) {
        tempErrors.password =
          "The password must be at least 8 characters long.";
      } else if (!/[A-Z]/.test(formData.passwordInfo.password)) {
        tempErrors.password = "Password must include an uppercase letter.";
      } else if (!/[a-z]/.test(formData.passwordInfo.password)) {
        tempErrors.password = "Password must include a lowercase letter.";
      } else if (
        !/[!@#$%^&*(),.?":{}|<>]/.test(formData.passwordInfo.password)
      ) {
        tempErrors.password = "Password must include a special character.";
      }

      if (!formData.passwordInfo.password_confirmation)
        tempErrors.password_confirmation = " Confirm Password is required";
      else if (
        formData.passwordInfo.password !== formData.passwordInfo.password_confirmation
      )
        tempErrors.password_confirmation = "Passwords do not match";
      if (!formData.passwordInfo.captcha)
        tempErrors.captcha = "Verify the captcha";
    }

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleOrgConfirm = (selectedOrgCode) => {
    // Save orgCode
    setFormData(prev => ({
      ...prev,
      organizationInfo: {
        ...prev.organizationInfo,
        orgCode: selectedOrgCode,
      },
    }));
    setShowOrgDetails(false);
    setSteps(4);
  };

  async function register() {
    setIsLoading(true);
    try {
      await axios.get(`/sanctum/csrf-cookie`, {});
      let registrationData = { ...formData };
      if (formData.organizationInfo.orgCode) {
        registrationData = {
          ...formData,
          organizationInfo: {
            orgCode: formData.organizationInfo.orgCode,
            orgName: "",
            address: "",
            city: "",
            state: "",
            zip: "",
            country: "",
          },
        };
      };

      const response = await axios.post(`/register`, registrationData);
      if (response.data.success) {
        setIsRegistered(true);
      } else {
        setErrors({ general: response.data.message || "Registration failed. Please try again." });
      }
    } catch (error) {
      console.error("Response:", error.response?.data);
      if (error.response?.data?.errors) {
        console.log("Validation errors:", error.response.data.errors);
        const errorsObj = error.response.data.errors;
        const combinedMessage = Object.values(errorsObj).join("\n");
        setErrors({ general: combinedMessage });
      } else if (error.response?.data?.message) {
        console.log("Error message:", error.response.data.message);
        setErrors({ general: error.response.data.message });
      } else {
        setErrors({ general: "Registration failed. Please try again." });
      }
    } finally {
      captchaRef.current.reset();
      setIsLoading(false);
      setFormData(prev => ({
        ...prev,
        passwordInfo: {
          ...prev.passwordInfo,
          captcha: "",
        },
      }));
    }
  }
  if (isRegistered) {
    return <Thankyou />;
  }
  const renderSteps = () => {
    switch (step) {
      case 1:
        return (
          <TermsofUse
            formData={formData}
            updateFormData={updateFormData}
            errors={errors}
          />
        );
      case 2:
        return (
          <Personal
            formData={formData}
            updateFormData={updateFormData}
            errors={errors}
            requiresCaptcha={emailCheckRequireCaptcha}
            onEmailCaptchaChange={setEmailCaptchaToken}
            captchaRef={emailCaptchaRef}
          />
        );
      case 3:
        return (
          <Organization
            formData={formData}
            updateFormData={updateFormData}
            errors={errors}
          />
        );
      case 4:
        return (
          <Password
            formData={formData}
            updateFormData={updateFormData}
            errors={errors}
            captchaRef={captchaRef}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <section className="fullScreenLayout">
        <NavbarGuest />
        <div
          className="d-flex justify-content-center align-items-center"
          style={{
            padding: "20px",
            flex: "1",
          }}
        >
          <div
            style={{
              margin: "auto",
              maxWidth: "450px",
              width: "100%",
            }}
          >
            <div
              className="d-flex justify-content-between"
              style={{ position: "relative" }}
            >
              <Progress
                totalSteps={totalSteps}
                step={step}
                className="progress "
              />
              <div className={`${step >= 1 ? "circle active" : "circle"}`}>
                1
              </div>
              <div className={`${step >= 2 ? "circle active" : "circle"}`}>
                2
              </div>
              <div className={`${step >= 3 ? "circle active" : "circle"}`}>
                3
              </div>
              <div className={`${step >= 4 ? "circle active" : "circle"}`}>
                4
              </div>
            </div>

            {renderSteps()}
            {errors.general && (
              <p className="error-text text-center">{errors.general}</p>
            )}
            <div className={`mb-2 d-flex ${isMobile ? "flex-column" : "justify-content-between"}`}>
              <button
                className={`${isMobile ? "mb-1" : ""} ${step <= 1 ? "hidden" : "back-btn"
                  } btn `}
                onClick={handlePrev}
                disabled={isLoading}
                style={{ width: isMobile ? "100%" : "calc( 50% - 5px )" }}
              // style={{ visibility: step <= 1 ? "hidden" : "visible" }}
              >
                Back
              </button>

              <button
                className={`${isMobile ? "mb-1" : ""} btn ${step >= totalSteps
                  ? "register-btn"
                  : step <= 1
                    ? "next-btn-first"
                    : "next-btn"
                  }`}
                onClick={handleNext}
                disabled={isLoading}
                style={{ width: isMobile ? "100%" : "calc( 50% - 5px )" }}
              >
                {isLoading
                  ? "Processing..."
                  : step < totalSteps
                    ? "Next"
                    : "Register"}
              </button>

            </div>

            <div
              className={`${step > 1 ? "hidden" : "text-center mt-4 mb-2 review-text"
                }`}
            >
              <span>
                Already have an account?{" "}
                <Link to="/login" className="review-text">
                  Log In
                </Link>
              </span>
            </div>
          </div>
          {showOrgDetails && (
            <OrgDetailsDialog
              show={showOrgDetails}
              onClose={handleCloseOrgDeatailsDialog}
              data={formData.organizationInfo}
              organizations={orgData}
              onConfirmOrg={handleOrgConfirm}
            />
          )}
        </div>
        <Footer />
      </section>
    </>
  );
}
